import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

const STORAGE_KEY = 'pv-blur';

interface BlurContextValue {
  blurred: boolean;
  toggle: () => void;
  setBlurred: (next: boolean) => void;
}

const BlurContext = createContext<BlurContextValue | null>(null);

function readStored(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(STORAGE_KEY) === '1';
}

export function BlurProvider({ children }: { children: ReactNode }) {
  const [blurred, setBlurredState] = useState<boolean>(() => readStored());

  const setBlurred = useCallback((next: boolean) => {
    if (next) window.localStorage.setItem(STORAGE_KEY, '1');
    else window.localStorage.removeItem(STORAGE_KEY);
    setBlurredState(next);
  }, []);

  const toggle = useCallback(() => {
    setBlurred(!blurred);
  }, [blurred, setBlurred]);

  const value = useMemo<BlurContextValue>(
    () => ({ blurred, toggle, setBlurred }),
    [blurred, toggle, setBlurred],
  );

  return <BlurContext.Provider value={value}>{children}</BlurContext.Provider>;
}

export function useBlur(): BlurContextValue {
  const ctx = useContext(BlurContext);
  if (!ctx) throw new Error('useBlur must be used within BlurProvider');
  return ctx;
}
